import { Handshake, Payment, People, Publish } from "@mui/icons-material";
import { Theme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import {
  AccordionConfig,
  InvoicePartyData,
  PartnerChannelData,
  PartnerData,
} from "./types";

export const InvoiceVisibleColumns = [
  "uid",
  "name",
  "company",
  "emailAddress",
  "account",
  "activityState",
  "campaignLimitedDataTransfer",
  "updatedAt",
];

export const apiEndpoint = "/data-push";

export const InvoicePartiesColumns: GridColDef<InvoicePartyData>[] = [
  {
    field: "uid",
    headerName: "UID",
    flex: 0.5,
    minWidth: 80,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "name",
    headerName: "Name",
    flex: 1,
    minWidth: 150,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "company",
    headerName: "Company",
    flex: 1,
    minWidth: 120,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "emailAddress",
    headerName: "Email",
    flex: 1,
    minWidth: 180,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "account",
    headerName: "Account",
    flex: 0.5,
    minWidth: 80,
    sortable: false,
    filterable: false,
    hideable: false,
  },
];

export const PartnerChannelVisibleColumns = [
  "channelId",
  "channelName",
  "subChannel",
  "mainChannel",
  "ggsChannel",
  "updatedAt",
];

export const PartnerChannelColumns: GridColDef<PartnerChannelData>[] = [
  {
    field: "channelId",
    headerName: "Channel ID",
    flex: 0.5,
    minWidth: 80,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "channelName",
    headerName: "Channel Name",
    flex: 1,
    minWidth: 150,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "subChannel",
    headerName: "Sub Channel",
    flex: 1,
    minWidth: 120,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "mainChannel",
    headerName: "Main Channel",
    flex: 1,
    minWidth: 120,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "ggsChannel",
    headerName: "GGS Channel",
    flex: 1,
    minWidth: 120,
    sortable: false,
    filterable: false,
    hideable: false,
  },
];

export const PublisherVisibleColumns = [
  "id",
  "name",
  "jsonConfig",
  "createdAt",
  "updatedAt",
];

export const PartnerVisibleColumns = [
  "referrerID",
  "username",
  "platform",
  "device",
  "invoiceParty",
  "publisherID",
  "partnerProgram",
  "partnerChannel",
  "updatedAt",
];

export const PartnerColumns: GridColDef<PartnerData>[] = [
  {
    field: "referrerID",
    headerName: "Referrer ID",
    flex: 0.5,
    minWidth: 80,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "username",
    headerName: "Username",
    flex: 1,
    minWidth: 120,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "platform",
    headerName: "Platform",
    flex: 0.5,
    minWidth: 80,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "device",
    headerName: "Device",
    flex: 0.5,
    minWidth: 80,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "invoiceParty",
    headerName: "Invoice Party",
    flex: 1,
    minWidth: 150,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "publisherID",
    headerName: "Publisher ID",
    flex: 0.5,
    minWidth: 80,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "partnerProgram",
    headerName: "Partner Program",
    flex: 0.5,
    minWidth: 100,
    sortable: false,
    filterable: false,
    hideable: false,
  },
  {
    field: "partnerChannel",
    headerName: "Partner Channel",
    flex: 0.5,
    minWidth: 100,
    sortable: false,
    filterable: false,
    hideable: false,
  },
];

export const accordionStyles = {
  root: {
    "&:before": { display: "none" },
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 0,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: "hidden",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
      transform: "translateY(-2px)",
      borderColor: "primary.light",
    },
  },
  summary: (isExpanded: boolean) => (theme: Theme) => {
    const { palette } = theme;
    const isLight = palette.mode === "light";
    const headerBg = isLight ? palette.primary.lighter : palette.primary.light;
    const headerFg = isLight ? palette.text.primary : palette.common.white;

    return {
      bgcolor: isExpanded ? headerBg : "background.paper",
      borderRadius: 0,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      minHeight: 56,
      "&:hover": {
        bgcolor: isExpanded ? headerBg : palette.action.hover,
      },
      "& .MuiAccordionSummary-content": {
        margin: 0,
        my: 1,
      },
      "& .MuiSvgIcon-root": {
        color: isExpanded ? headerFg : "text.secondary",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        fontSize: "1.5rem",
      },
    };
  },
  chip: {
    fontWeight: 600,
    height: 24,
    fontSize: "0.75rem",
    borderRadius: 1.5,
    "& .MuiChip-label": {
      fontWeight: 600,
      px: 1.5,
    },
  },
  icon: {
    color: "primary.main",
    fontSize: "1.75rem",
  },
  typography: (theme: Theme) => ({
    fontWeight: 600,
    fontSize: "16px",
    color:
      theme.palette.mode === "light"
        ? theme.palette.text.primary
        : theme.palette.common.white,
  }),
};

export const PUSH_MODULES = {
  INVOICE_PARTIES: "invoice-parties",
  PARTNER_CHANNELS: "partner-channels",
  PUBLISHERS: "publishers",
  PARTNERS: "partners",
} as const;

export const accordionConfigs: AccordionConfig[] = [
  {
    id: PUSH_MODULES.INVOICE_PARTIES,
    title: "Invoice Parties",
    icon: Payment,
  },
  {
    id: PUSH_MODULES.PARTNER_CHANNELS,
    title: "Partner Channels",
    icon: Handshake,
  },
  { id: PUSH_MODULES.PUBLISHERS, title: "Publishers", icon: Publish },
  { id: PUSH_MODULES.PARTNERS, title: "Partners", icon: People },
];
